// ============================================================
// api/adminContactMessages.js
// Endpoint bindings for the mgmt/admin/contact-messages endpoints
// (prospect enquiries from the marketing /contact form). Admin-tenant
// JWT, same client as every other admin binding.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-contact-messages-list.js
//   backend/func-carehub-api/src/functions/admin-contact-messages-patch.js
// ============================================================

import { apiGet, apiPatch } from './client';

// Paths are relative to the API base and must NOT carry an `/api` prefix —
// the client adds it. Passing `/api/...` produces `/carehub/api/api/...`,
// which 404s at the gateway. Every other binding in this folder is written
// this way; this one was not, and shipped broken.

/**
 * List contact messages (newest first) with pagination + optional
 * status filter. statusCounts comes back UNFILTERED on every call so
 * the sidebar badge can read it from any list response.
 *
 * @param {Object} [params={}]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=50]
 * @param {('new'|'handled')} [params.status]
 *
 * @returns {Promise<{
 *   items: Array<Object>, totalCount: number, page: number, pageSize: number,
 *   statusCounts: { new: number, handled: number }
 * }>}
 */
export function listAdminContactMessages(params = {}) {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  if (params.status) qs.set('status', params.status);
  const q = qs.toString();
  return apiGet(`/mgmt/admin/contact-messages${q ? `?${q}` : ''}`);
}

/**
 * Mark an enquiry handled or reopen it, and/or set the internal note.
 * HandledBy / HandledAt are stamped by the SERVER from the caller's
 * identity — they are not sent from here.
 *
 * @param {number} contactMessageId
 * @param {{ status?: 'new'|'handled', notes?: string|null }} body
 */
export function patchAdminContactMessage(contactMessageId, body) {
  return apiPatch(`/mgmt/admin/contact-messages/${encodeURIComponent(contactMessageId)}`, body);
}
